import {Component, HostListener, OnInit} from '@angular/core';
import {ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {Store} from '@ngxs/store';
import {CommandService} from '@app/services/command.service';
import {AccessType, ConnectionStatus, KeyCode, LogLevel, LogResource} from '@app/modules/shared/data/constants';
import {GameModeAction, GameModeModel, GameModeState} from '@app/state/game-mode.state';
import {
  TeamInfoControlComponent
} from '@app/modules/shared/components/dialogs/team-info-control/team-info-control.component';
import {LogService} from '@app/services/log.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {WrtcService} from '@app/modules/wrtc/services/wrtc.service';
import {ApplauseLevel, CoreService} from '@app/services/core.service';
import {ChatService} from '@app/modules/chat/services/chat.service';
import {RemoteParticipantInfo} from '@app/models/remote-participant-info';
import {RemoteUsersInfoState} from '@app/state/remote-users.state';
import {UsersListService} from '@app/modules/shared/services/users-list.service';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {LoginService} from '@app/modules/auth/services/login.service';
import {CurrentClass} from '@app/models/current-class';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.css']
})
export class UsersListComponent implements OnInit {
  // public remoteUsersByTeam: { [key: number]: RemoteParticipantInfo [] } = {};
  public classroom: ClassroomStateModel = ClassroomState.defaults;
  public gameState: GameModeModel;
  public localUser: LocalUser;
  public status = {
    isOpenRedirect: false
  };
  public classroomNumber;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  protected readonly AccessType = AccessType;
  protected readonly ConnectionStatus = ConnectionStatus;


  constructor(
    private store: Store,
    private commandService: CommandService,
    private wrtcService: WrtcService,
    private dialogService: NgbModal,
    private chatService: ChatService,
    private coreService: CoreService,
    protected usersListService: UsersListService,
    private loginService: LoginService
  ) {}

  public ngOnInit(): void {
    this.store.select(ClassroomState).subscribe(roomState => {
      this.classroom = roomState;
    });

    this.store.select(GameModeState).subscribe(gameModeState => {
      this.gameState = gameModeState;
      this.usersListService.sortUsers();
    });

    this.store.select(LocalUserState.getLocalUser).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });

    this.store.select(RemoteUsersInfoState.getUsersInfo).subscribe(() => {
      this.usersListService.sortUsers();
    });
  }

  public removeScoreFromTeam(teamId): void {
    if (this.gameState.scoreByTeamId[teamId] >= 1) {
      this.coreService.sendTeamScore(teamId, this.gameState.scoreByTeamId[teamId] - 1).subscribe();
    }
  }

  public addScoreToTeam(teamId): void {
    this.coreService.sendTeamScore(teamId, this.gameState.scoreByTeamId[teamId] + 1).subscribe();
  }

  public toggleTeamTalk(): void {
    if (this.localUser.accessType !== AccessType.STUDENT && this.localUser.accessType !== AccessType.OBSERVER) {
      this.store.dispatch(new GameModeAction.ToggleTeamTalk(!this.gameState.isTeamTalkOn));
      this.coreService.sendTeamTalk(this.gameState.isTeamTalkOn).subscribe();
    }
  }

  public openEditTeamsDialog(): void {
    const teams = this.gameState.teams.map(team => ({id: team.id, name: team.name, colorIndex: team.colorIndex}));
    const userIdWithTeamIdList = this.usersListService.remoteUsersWrapper.students
      .filter(student => student.additionalInfo.roomConnectionStatus === ConnectionStatus.ONLINE)
      .map(student => {
        const id = student.id;
        const item = {id, teamId: -1, displayName: ''};
        item.id = id;
        item.displayName = student.displayName;

        if (this.gameState.participantsTeamIndexById[id] !== undefined) {
          const teamIndex = this.gameState.participantsTeamIndexById[id];
          item.teamId = teams[teamIndex].id;
        } else {
          item.teamId = -1;
        }
        return item;
      });

    const dialog: NgbModalRef = this.dialogService.open(TeamInfoControlComponent, { size: 'lg' });

    dialog.componentInstance.usersList = userIdWithTeamIdList;
    dialog.componentInstance.teams = teams;
    dialog.componentInstance.isInCreateMode = false;

    dialog.result.then((result) => {
      this.coreService.sendTeamsInfo(result.teams, result.lastTeamId).subscribe();
    }, (reason) => {
      console.log('dialog closed ' + reason);
      LogService.writeLog(`Dialog is closed`, LogLevel.INFO, LogResource.WRTC);
    });
  }

  public playApplauseAudio(id: number){
    this.coreService.applause(ApplauseLevel.TEAM, id);
    this.coreService.sendTeamApplause(id).subscribe();
  }

  public isApplauseSoundPlaying(teamId: number): boolean {
    return this.coreService.applauseAudio.team.isPlaying && teamId === this.coreService.applauseAudio.team.id;
  }

  public cleanAllMessages(): void {
    this.chatService.sendClearMessages().subscribe();
  }

  @HostListener('window:keydown', ['$event'])
  public keyDown(event: KeyboardEvent): void {
    if (event.altKey && event.code === KeyCode.KEY_C){
      this.cleanAllMessages();
    }
  }

  public resetAllCorrectCounters(): void {
    if (window.confirm('Are you sure you want to reset all counters?')) {
      this.chatService.sendResetScore(-1).subscribe();
    }
  }


  public redirectClassroom(): void {
    if (this.classroomNumber > 99 && this.classroomNumber < 10000) {
      this.coreService.sendRedirectClassroom(this.classroomNumber).subscribe();
    }
  }

  public resetWebRTCCall(): void {
    this.wrtcService.sendReset().subscribe();
    if (this.gameState.isStarted) {
      this.coreService.sendGameMode(false).subscribe();
    }
  }

  public resetClassroom(): void {
    const currentClass: CurrentClass = this.classroom.currentClass;
    this.coreService.sendResetLesson().subscribe(() => {
      this.loginService.selectAccessType(this.localUser.accessType).subscribe(() => {
        if (currentClass) {
          const subscription: Subscription = this.coreService.afterResetEmitter$.subscribe((): void => {
            this.coreService.sendSetClass(currentClass).subscribe(() => {
              this.coreService.sendToggleDND(!!currentClass.groupId).subscribe();
            });
            subscription.unsubscribe();
          });
        }
        this.coreService.sendResetUsers().subscribe();
      });
    });
  }

  public addTeamSticker(id: number): void {
    this.coreService.sendAddTeamSticker(id).subscribe();
  }

  public removeTeamSticker(id: number): void {
    this.coreService.sendRemoveTeamSticker(id).subscribe();
  }
}
